import React from "react";
import { formatDate } from "@/utils/timezone";
import { formatCurrency, decodeHtmlEntities } from "@/utils/formatters";
import BaseModal from "./BaseModal";

const SENTIMENT_COLORS = {
  bullish: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  bearish: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  neutral: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

const CONVICTION_COLORS = {
  high: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  low: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

const AlertSourceModal = ({ alert, onClose }) => {
  if (!alert) return null;

  const isAbove = alert.alert_type === "PRICE_ABOVE";
  const source = alert.source_entity;
  const hasIntelSource = alert.source_type === "intel" && source;
  const pc = alert.price_change_since_post;

  const alertTypeBadgeColor = isAbove
    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
    : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";

  const statusBadgeColor = alert.triggered_at
    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
    : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";

  const header = (
    <div className="flex items-center justify-between p-6">
      <div className="flex items-center gap-3">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {alert.ticker}
        </h2>
        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${alertTypeBadgeColor}`}>
          {isAbove ? "Above" : "Below"} {formatCurrency(alert.target_price)}
        </span>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadgeColor}`}>
          {alert.triggered_at ? "Triggered" : "Active"}
        </span>
      </div>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-end gap-3 p-6">
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
      >
        Close
      </button>
    </div>
  );

  return (
    <BaseModal header={header} footer={footer} onClose={onClose}>
      <div className="p-6 space-y-6">

        {/* Price change since intel was captured */}
        {pc && hasIntelSource && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Price Since Intel ({formatDate(source.captured_at)})
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(pc.current_price)}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    from {formatCurrency(pc.price_at_date)}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${pc.change_percent >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                  {pc.change_percent >= 0 ? "+" : ""}{pc.change_percent.toFixed(2)}%
                </div>
                <div className={`text-sm ${pc.change_amount >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                  {pc.change_amount >= 0 ? "+" : ""}{formatCurrency(Math.abs(pc.change_amount))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alert target */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Alert Condition
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Trigger</p>
              <p className={`text-lg font-bold ${isAbove ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                {isAbove ? "Above" : "Below"} {formatCurrency(alert.target_price)}
              </p>
            </div>
            {hasIntelSource && source.price_at_analysis && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Price at Signal</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {formatCurrency(source.price_at_analysis)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Source intel */}
        {hasIntelSource && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Intelligence Source
            </h3>
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(source.captured_at)} • {source.platform || "manual"}
                </p>
                <div className="flex items-center gap-1.5">
                  {source.sentiment && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SENTIMENT_COLORS[source.sentiment] || SENTIMENT_COLORS.neutral}`}>
                      {source.sentiment}
                    </span>
                  )}
                  {source.conviction && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CONVICTION_COLORS[source.conviction] || CONVICTION_COLORS.low}`}>
                      {source.conviction}
                    </span>
                  )}
                </div>
              </div>
              {source.thesis && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  {decodeHtmlEntities(source.thesis)}
                </p>
              )}
              {source.author && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  by {source.author}
                </p>
              )}
              {source.catalysts && source.catalysts.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {source.catalysts.map((c, i) => (
                    <span key={i} className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Source</p>
            <p className="font-medium text-gray-900 dark:text-white capitalize">
              {alert.source_type}
            </p>
          </div>
          {hasIntelSource && source.conviction && (
            <div>
              <p className="text-gray-500 dark:text-gray-400">Conviction</p>
              <p className="font-medium text-gray-900 dark:text-white capitalize">
                {source.conviction}
              </p>
            </div>
          )}
          <div>
            <p className="text-gray-500 dark:text-gray-400">Created</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {formatDate(alert.created_at)}
            </p>
          </div>
          {alert.triggered_at && (
            <div>
              <p className="text-gray-500 dark:text-gray-400">Triggered</p>
              <p className="font-medium text-amber-600 dark:text-amber-400">
                {formatDate(alert.triggered_at)}
              </p>
            </div>
          )}
        </div>

      </div>
    </BaseModal>
  );
};

export default AlertSourceModal;
